import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  SelectChangeEvent,
  CircularProgress,
  Snackbar,
  Card,
  CardContent,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Fade,
  Slide,
  Container,
  Stack,
  alpha
} from '@mui/material';
import {
  Save as SaveIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { LockerSettings } from '../../stubs/settings';
import { settingsService } from '../../services/api';
import { useCallback } from 'react';

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const [ settings, setSettings ] = useState<LockerSettings>( {
    minDurationBookLocker: 0,
    maxDurationBookLocker: 0,
    maxResponseTime: 0,
    extendLockerDurarionBefore: 0,
    maxOfferAcceptanceTime: 0,
    maxLockerBookingTime: 0,
    maxItemSubmitTime: 0,
    maxItemCollectTime: 0,

  } );
  const [ loading, setLoading ] = useState( true );
  const [ saving, setSaving ] = useState( false );
  const [ hasChanges, setHasChanges ] = useState( false );
  const [ originalSettings, setOriginalSettings ] = useState<LockerSettings | null>( null );
  const [ toast, setToast ] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>( {
    open: false,
    message: '',
    severity: 'success'
  } );

  const convertMinutesToHours = ( minutes: number ): number => {
    return Number( ( minutes / 60 ).toFixed( 2 ) );
  };

  const convertToMinutes = ( hours: number ): number => {
    return Math.round( hours * 60 );
  };

  const fetchSettings = useCallback( async () => {
    try {
      setLoading( true );
      const response = await settingsService.fetchSettings();


      const data = response?.data || response;
      if ( data ) {
        const newSettings = {
          minDurationBookLocker: convertMinutesToHours( Number( data.minDurationBookLocker ) ),
          maxDurationBookLocker: convertMinutesToHours( Number( data.maxDurationBookLocker ) ),
          maxResponseTime: convertMinutesToHours( Number( data.maxResponseTime ) ),
          extendLockerDurarionBefore: convertMinutesToHours( Number( data.extendLockerDurarionBefore ) ),
          maxLockerBookingTime: convertMinutesToHours( Number( data.maxLockerBookingTime ) ),
          maxOfferAcceptanceTime: convertMinutesToHours( Number( data.maxOfferAcceptanceTime ) ),
          maxItemSubmitTime: convertMinutesToHours( Number( data.maxItemSubmitTime ) ),
          maxItemCollectTime: convertMinutesToHours( Number( data.maxItemCollectTime ) ),

        };
    
        setSettings( newSettings );
        setOriginalSettings( newSettings );
      } else {
        setToast( {
          open: true,
          message: 'Error fetching settings — empty response',
          severity: 'error'
        } );
      }
    } catch ( error ) {
      console.error( 'Error fetching settings:', error );
      setToast( {
        open: true,
        message: 'Error fetching settings',
        severity: 'error'
      } );
    } finally {
      setLoading( false );
    }
  }, [] ); // ✅ dependencies (none for now)


  useEffect( () => {
    fetchSettings();
  }, [ fetchSettings ] ); // ✅ no more warning

  useEffect( () => {
    if ( originalSettings ) {
      const changed = JSON.stringify( settings ) !== JSON.stringify( originalSettings );
      setHasChanges( changed );
    }
  }, [ settings, originalSettings ] );

  const handleChange = ( field: keyof LockerSettings ) => (
    event: SelectChangeEvent
  ) => {
    setSettings( {
      ...settings,
      [ field ]: Number( event.target.value )
    } );
  };

  const handleSubmit = async ( event: React.FormEvent ) => {
    event.preventDefault();
    try {
      setSaving( true );
      const response = await settingsService.createUpdateSettings( {
        minDurationBookLocker: convertToMinutes( settings.minDurationBookLocker ).toString(),
        maxDurationBookLocker: convertToMinutes( settings.maxDurationBookLocker ).toString(),
        maxResponseTime: convertToMinutes( settings.maxResponseTime ).toString(),
        extendLockerDurarionBefore: convertToMinutes( settings.extendLockerDurarionBefore ).toString(),
        maxLockerBookingTime: convertToMinutes( settings.maxLockerBookingTime ).toString(),
        maxOfferAcceptanceTime: convertToMinutes( settings.maxOfferAcceptanceTime ).toString(),
        maxOfferSubmissionTime: convertToMinutes( settings.maxItemSubmitTime ).toString(),
        maxOfferCollectTime: convertToMinutes( settings.maxItemCollectTime ).toString(),
      } );

      if ( response.success ) {
        setOriginalSettings( settings );
        setHasChanges( false );
        setToast( {
          open: true,
          message: 'Settings updated successfully',
          severity: 'success'
        } );
      } else {
        setToast( {
          open: true,
          message: response.message || 'Error updating settings',
          severity: 'error'
        } );
      }
    } catch ( error ) {
      console.error( 'Error updating settings:', error );
      setToast( {
        open: true,
        message: 'Error updating settings',
        severity: 'error'
      } );
    } finally {
      setSaving( false );
    }
  };

  const handleReset = () => {
    if ( originalSettings ) {
      setSettings( originalSettings );
      setHasChanges( false );
    }
  };

  const handleCloseToast = () => {
    setToast( { ...toast, open: false } );
  };

  const getTimeOptions = ( type: 'short' | 'medium' | 'long' | 'new' ) => {
    const options = [];

    if ( type === 'short' ) {
      options.push(
        { value: 0.08, label: '5 minutes' },
        { value: 0.17, label: '10 minutes' },
        { value: 0.25, label: '15 minutes' },
        { value: 0.5, label: '30 minutes' },
        { value: 0.75, label: '45 minutes' },
        { value: 1, label: '1 hour' },
        { value: 1.5, label: '1.5 hours' },
        { value: 2, label: '2 hours' }
      );
    } else if ( type === 'medium' ) {
      options.push(
        { value: 0.08, label: '5 minutes' },
        { value: 0.17, label: '10 minutes' },
        { value: 0.25, label: '15 minutes' },
        { value: 0.5, label: '30 minutes' },
        { value: 0.75, label: '45 minutes' },
        { value: 1, label: '1 hour' },
        { value: 2, label: '2 hours' },
        { value: 4, label: '4 hours' },
        { value: 6, label: '6 hours' },
        { value: 8, label: '8 hours' }
      );
    } else if ( type === 'new' ) {
      options.push(
        { value: 12, label: '12 hours' },
        { value: 24, label: '1 day (24 hours)' },


        { value: 48, label: '2 days (48 hours)' },

        { value: 72, label: '3 days (72 hours)' },
        { value: 96, label: '4 days (96 hours)' },
        { value: 120, label: '5 days (120 hours)' },
        { value: 144, label: '6 days (144 hours)' }
      );
    } else {
      options.push(
        { value: 0.08, label: '5 minutes' },
        { value: 0.17, label: '10 minutes' },
        { value: 0.25, label: '15 mintes' },
        { value: 0.5, label: '30 minutes' },
        { value: 0.75, label: '45 minutes' },
        { value: 1, label: '1 hour' },
        { value: 2, label: '2 hours' },
        { value: 4, label: '4 hours' },
        { value: 6, label: '6 hours' },
        { value: 8, label: '8 hours' },
        { value: 12, label: '12 hours' },
        { value: 24, label: '24 hours' }
      );
    }

    return options;
  };

  if ( loading ) {
    return (
      <Container maxWidth="lg" sx={ { py: 4 } }>
        <Box sx={ {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: 2
        } }>
          <CircularProgress size={ 60 } />
          <Typography variant="h6" color="text.secondary">
            Loading settings...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={ { py: 4 } }>
      <Slide direction="down" in={ true } timeout={ 600 }>
        <Box>
          {/* Header Section */ }
          <Box sx={ { mb: 4 } }>
            <Stack direction="row" alignItems="center" spacing={ 2 } sx={ { mb: 2 } }>
              <Box sx={ {
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              } }>
                <SettingsIcon sx={ { fontSize: 28 } } />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  { t( 'settings.title' ) }
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Configure locker booking and management settings
                </Typography>
              </Box>
            </Stack>

            { hasChanges && (
              <Fade in={ hasChanges }>
                <Alert
                  severity="warning"
                  sx={ {
                    mb: 2,
                    '& .MuiAlert-icon': { alignItems: 'center' }
                  } }
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={ handleReset }
                      startIcon={ <RefreshIcon /> }
                    >
                      Reset
                    </Button>
                  }
                >
                  You have unsaved changes. Save your changes or reset to discard them.
                </Alert>
              </Fade>
            ) }
          </Box>

          {/* Settings Form */ }
          <Paper
            elevation={ 0 }
            sx={ {
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            } }
          >
            <Box sx={ {
              p: 3,
              bgcolor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            } }>
              <SettingsIcon />
              <Typography variant="h6" fontWeight="600">
                { t( 'settings.locker.title' ) }
              </Typography>
            </Box>

            <form onSubmit={ handleSubmit }>
              <CardContent sx={ { p: 4 } }>
                <Grid container spacing={ 4 }>
                  {/* Minimum Duration */ }
                  <Grid item xs={ 12 } md={ 6 }>
                    <Card
                      elevation={ 0 }
                      sx={ {
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: ( theme ) => `0 4px 20px ${ alpha( theme.palette.primary.main, 0.1 ) }`
                        },
                        transition: 'all 0.3s ease'
                      } }
                    >
                      <CardContent sx={ { p: 3 } }>
                        <Stack direction="row" alignItems="center" spacing={ 1 } sx={ { mb: 2 } }>
                          <Typography variant="h6" fontWeight="600">
                            Minimum Duration
                          </Typography>
                          <Tooltip title="Minimum time required to book a locker">
                            <IconButton size="small" sx={ { color: 'text.secondary' } }>
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={ { mb: 2 } }>
                          Set the minimum duration required for locker bookings
                        </Typography>
                        <FormControl fullWidth>
                          <InputLabel>Minimum Duration</InputLabel>
                          <Select
                            value={ settings.minDurationBookLocker.toString() }
                            label="Minimum Duration"
                            onChange={ handleChange( 'minDurationBookLocker' ) }
                            sx={ {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'divider'
                              }
                            } }
                          >
                            { getTimeOptions( 'short' ).map( ( option ) => (
                              <MenuItem key={ option.value } value={ option.value }>
                                { option.label }
                              </MenuItem>
                            ) ) }
                          </Select>
                        </FormControl>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Maximum Duration */ }
                  <Grid item xs={ 12 } md={ 6 }>
                    <Card
                      elevation={ 0 }
                      sx={ {
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: ( theme ) => `0 4px 20px ${ alpha( theme.palette.primary.main, 0.1 ) }`
                        },
                        transition: 'all 0.3s ease'
                      } }
                    >
                      <CardContent sx={ { p: 3 } }>
                        <Stack direction="row" alignItems="center" spacing={ 1 } sx={ { mb: 2 } }>
                          <Typography variant="h6" fontWeight="600">
                            Maximum Duration
                          </Typography>
                          <Tooltip title="Maximum time allowed for locker bookings">
                            <IconButton size="small" sx={ { color: 'text.secondary' } }>
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={ { mb: 2 } }>
                          Set the maximum duration allowed for locker bookings
                        </Typography>
                        <FormControl fullWidth>
                          <InputLabel shrink>Maximum Duration</InputLabel>
                          <Select
                            value={ settings.maxDurationBookLocker.toString() }
                            label="Maximum Duration"
                            onChange={ handleChange( 'maxDurationBookLocker' ) }
                            sx={ {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'divider'
                              }
                            } }
                          >
                            { getTimeOptions( 'long' ).map( ( option ) => (
                              <MenuItem key={ option.value } value={ option.value }>
                                { option.label }
                              </MenuItem>
                            ) ) }
                          </Select>
                        </FormControl>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Maximum Response Time */ }
                  <Grid item xs={ 12 } md={ 6 }>
                    <Card
                      elevation={ 0 }
                      sx={ {
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: ( theme ) => `0 4px 20px ${ alpha( theme.palette.primary.main, 0.1 ) }`
                        },
                        transition: 'all 0.3s ease'
                      } }
                    >
                      <CardContent sx={ { p: 3 } }>
                        <Stack direction="row" alignItems="center" spacing={ 1 } sx={ { mb: 2 } }>
                          <Typography variant="h6" fontWeight="600">
                            Response Time Limit
                          </Typography>
                          <Tooltip title="Maximum time for sellers and buyers to respond">
                            <IconButton size="small" sx={ { color: 'text.secondary' } }>
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={ { mb: 2 } }>
                          Maximum time allowed for users to respond to actions
                        </Typography>
                        <FormControl fullWidth>
                          <InputLabel>Response Time Limit</InputLabel>
                          <Select
                            value={ settings.maxResponseTime.toString() }
                            label="Response Time Limit"
                            onChange={ handleChange( 'maxResponseTime' ) }
                            sx={ {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'divider'
                              }
                            } }
                          >
                            { getTimeOptions( 'short' ).map( ( option ) => (
                              <MenuItem key={ option.value } value={ option.value }>
                                { option.label }
                              </MenuItem>
                            ) ) }
                          </Select>
                        </FormControl>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Extension Duration */ }
                  {/* <Grid item xs={ 12 } md={ 6 }>
                    <Card
                      elevation={ 0 }
                      sx={ {
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: ( theme ) => `0 4px 20px ${ alpha( theme.palette.primary.main, 0.1 ) }`
                        },
                        transition: 'all 0.3s ease'
                      } }
                    >
                      <CardContent sx={ { p: 3 } }>
                        <Stack direction="row" alignItems="center" spacing={ 1 } sx={ { mb: 2 } }>
                          <Typography variant="h6" fontWeight="600">
                            Extension Window
                          </Typography>
                          <Tooltip title="Time before expiry when users can extend locker duration">
                            <IconButton size="small" sx={ { color: 'text.secondary' } }>
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={ { mb: 2 } }>
                          Time before expiry when users can extend locker duration
                        </Typography>
                        <FormControl fullWidth>
                          <InputLabel>Extension Window</InputLabel>
                          <Select
                            value={ settings.extendLockerDurarionBefore.toString() }
                            label="Extension Window"
                            onChange={ handleChange( 'extendLockerDurarionBefore' ) }
                            sx={ {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'divider'
                              }
                            } }
                          >
                            { getTimeOptions( 'medium' ).map( ( option ) => (
                              <MenuItem key={ option.value } value={ option.value }>
                                { option.label }
                              </MenuItem>
                            ) ) }
                          </Select>
                        </FormControl>
                      </CardContent>
                    </Card>
                  </Grid> */}
                  {/* Maximum Response Time */ }
                  <Grid item xs={ 12 } md={ 6 }>
                    <Card
                      elevation={ 0 }
                      sx={ {
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: ( theme ) => `0 4px 20px ${ alpha( theme.palette.primary.main, 0.1 ) }`
                        },
                        transition: 'all 0.3s ease'
                      } }
                    >
                      <CardContent sx={ { p: 3 } }>
                        <Stack direction="row" alignItems="center" spacing={ 1 } sx={ { mb: 2 } }>
                          <Typography variant="h6" fontWeight="600">
                            Offer Acceptance Time
                          </Typography>
                          <Tooltip title="Maximum time for sellers and buyers to respond">
                            <IconButton size="small" sx={ { color: 'text.secondary' } }>
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={ { mb: 2 } }>
                          Set the minimum duration allowed for the buyer to accept an offer.
                        </Typography>
                        <FormControl fullWidth>
                          <InputLabel shrink>Offer Acceptance Time</InputLabel>
                          <Select
                            value={ settings.maxOfferAcceptanceTime.toString() }
                            label="Response Time Limit"
                            onChange={ handleChange( 'maxOfferAcceptanceTime' ) }
                            sx={ {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'divider'
                              }
                            } }
                          >
                            { getTimeOptions( 'new' ).map( ( option ) => (
                              <MenuItem key={ option.value } value={ option.value }>
                                { option.label }
                              </MenuItem>
                            ) ) }
                          </Select>
                        </FormControl>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Extension Duration */ }
                  <Grid item xs={ 12 } md={ 6 }>
                    <Card
                      elevation={ 0 }
                      sx={ {
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: ( theme ) => `0 4px 20px ${ alpha( theme.palette.primary.main, 0.1 ) }`
                        },
                        transition: 'all 0.3s ease'
                      } }
                    >
                      <CardContent sx={ { p: 3 } }>
                        <Stack direction="row" alignItems="center" spacing={ 1 } sx={ { mb: 2 } }>
                          <Typography variant="h6" fontWeight="600">
                            Maximum Locker Booking Time
                          </Typography>
                          <Tooltip title="Time before expiry when users can extend locker duration">
                            <IconButton size="small" sx={ { color: 'text.secondary' } }>
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={ { mb: 2 } }>
                          Set the maximum time within which the seller must book a locker.
                        </Typography>
                        <FormControl fullWidth>

                          <InputLabel shrink>Max Locker Book Time</InputLabel>
                          <Select
                            value={ settings.maxLockerBookingTime.toString() }
                            label="Extension Window"
                            onChange={ handleChange( 'maxLockerBookingTime' ) }
                            sx={ {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'divider'
                              }
                            } }
                          >
                            { getTimeOptions( 'new' ).map( ( option ) => (
                              <MenuItem key={ option.value } value={ option.value }>
                                { option.label }
                              </MenuItem>
                            ) ) }
                          </Select>
                        </FormControl>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={ 12 } md={ 6 }>
                    <Card
                      elevation={ 0 }
                      sx={ {
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: ( theme ) => `0 4px 20px ${ alpha( theme.palette.primary.main, 0.1 ) }`
                        },
                        transition: 'all 0.3s ease'
                      } }
                    >
                      <CardContent sx={ { p: 3 } }>
                        <Stack direction="row" alignItems="center" spacing={ 1 } sx={ { mb: 2 } }>
                          <Typography variant="h6" fontWeight="600">
                            Offer Submission Time
                          </Typography>
                          <Tooltip title="Maximum time allowed for locker bookings">
                            <IconButton size="small" sx={ { color: 'text.secondary' } }>
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={ { mb: 2 } }>
                          Set the duration allowed for the buyer and seller to return the item to support.
                        </Typography>
                        <FormControl fullWidth>
                          <InputLabel shrink>Offer Submission Time</InputLabel>
                          <Select
                            value={ settings.maxItemSubmitTime.toString() }
                            label="Response Time Limit"
                            onChange={ handleChange( 'maxItemSubmitTime' ) }
                            sx={ {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'divider'
                              }
                            } }
                          >
                            { getTimeOptions( 'new' ).map( ( option ) => (
                              <MenuItem key={ option.value } value={ option.value }>
                                { option.label }
                              </MenuItem>
                            ) ) }
                          </Select>
                        </FormControl>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Maximum Response Time */ }
                  {/* <Grid item xs={ 12 } md={ 6 }>
                    <Card
                      elevation={ 0 }
                      sx={ {
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: ( theme ) => `0 4px 20px ${ alpha( theme.palette.primary.main, 0.1 ) }`
                        },
                        transition: 'all 0.3s ease'
                      } }
                    >
                      <CardContent sx={ { p: 3 } }>
                        <Stack direction="row" alignItems="center" spacing={ 1 } sx={ { mb: 2 } }>
                          <Typography variant="h6" fontWeight="600">
                            Offer Collect Time
                          </Typography>
                          <Tooltip title="Maximum time for sellers and buyers to respond">
                            <IconButton size="small" sx={ { color: 'text.secondary' } }>
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={ { mb: 2 } }>
                          Set the duration allowed for the buyer and seller to collect the item from support.
                        </Typography>
                        <FormControl fullWidth>
                          <InputLabel shrink>Offer Collect Time</InputLabel>
                          <Select
                            value={ settings.maxItemCollectTime.toString() }
                            label="Response Time Limit"
                            onChange={ handleChange( 'maxItemCollectTime' ) }
                            sx={ {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'divider'
                              }
                            } }
                          >
                            { getTimeOptions( 'new' ).map( ( option ) => (
                              <MenuItem key={ option.value } value={ option.value }>
                                { option.label }
                              </MenuItem>
                            ) ) }
                          </Select>
                        </FormControl>
                      </CardContent>
                    </Card>
                  </Grid> */}
                </Grid>

                <Divider sx={ { my: 4 } } />

                {/* Action Buttons */ }
                <Box sx={ {
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                  alignItems: 'center'
                } }>
                  { hasChanges && (
                    <Chip
                      icon={ <WarningIcon /> }
                      label="Unsaved changes"
                      color="warning"
                      variant="outlined"
                      sx={ {
                        borderRadius: 2,
                        fontWeight: 500
                      } }
                    />
                  ) }
                  <Button
                    variant="outlined"
                    onClick={ handleReset }
                    disabled={ !hasChanges || saving }
                    sx={ {
                      minWidth: 120,
                      px: 3,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2
                      }
                    } }
                  >
                    Reset
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={ saving ? <CircularProgress size={ 20 } color="inherit" /> : <SaveIcon /> }
                    disabled={ saving || !hasChanges }
                    sx={ {
                      minWidth: 140,
                      px: 3,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)'
                      }
                    } }
                  >
                    { saving ? 'Saving...' : 'Save Changes' }
                  </Button>
                </Box>
              </CardContent>
            </form>
          </Paper>
        </Box>
      </Slide>

      {/* Success Toast */ }
      <Snackbar
        open={ toast.open }
        autoHideDuration={ 6000 }
        onClose={ handleCloseToast }
        anchorOrigin={ { vertical: 'top', horizontal: 'right' } }
        TransitionComponent={ Slide }
      >
        <Alert
          onClose={ handleCloseToast }
          severity={ toast.severity }
          sx={ {
            width: '100%',
            '& .MuiAlert-icon': { alignItems: 'center' }
          } }
          icon={ toast.severity === 'success' ? <CheckCircleIcon /> : undefined }
        >
          { toast.message }
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Settings; 